import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class BombeGiftOption extends HTMLElement {
  #form = null;
  #productForm = null;
  #checkbox = null;
  #note = null;
  #error = null;
  #nativeSubmitAction = '/handleSubmit';
  #submitting = false;

  connectedCallback() {
    this.#form = this.closest('form');
    this.#productForm = this.closest('product-form-component');
    this.#checkbox = this.querySelector('input[type="checkbox"]');
    this.#note = this.querySelector('textarea');
    this.#error = this.querySelector('[data-bombe-gift-error]');

    if (!this.#form || !this.#productForm || !this.#checkbox || !this.#note) return;

    this.#nativeSubmitAction = this.#productForm.getAttribute('on:submit') || '/handleSubmit';
    this.#checkbox.setAttribute('aria-controls', this.#note.id);
    this.#syncState();
    this.#checkbox.addEventListener('change', this.#handleChange);
    this.#form.addEventListener('submit', this.#handleSubmit, true);
  }

  disconnectedCallback() {
    this.#checkbox?.removeEventListener('change', this.#handleChange);
    this.#form?.removeEventListener('submit', this.#handleSubmit, true);
    if (this.#productForm && !this.#productForm.hasAttribute('on:submit')) {
      this.#productForm.setAttribute('on:submit', this.#nativeSubmitAction);
    }
  }

  #handleChange = () => {
    this.#syncState();
    this.#hideError();
  };

  #syncState() {
    const checked = Boolean(this.#checkbox?.checked);
    this.toggleAttribute('data-selected', checked);
    this.#checkbox?.setAttribute('aria-expanded', checked ? 'true' : 'false');
    if (this.#note) this.#note.disabled = !checked;

    if (this.#productForm) {
      if (checked) {
        this.#productForm.removeAttribute('on:submit');
      } else {
        this.#productForm.setAttribute('on:submit', this.#nativeSubmitAction);
      }
    }
  }

  #handleSubmit = async (event) => {
    if (!this.#checkbox?.checked) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.#submitting) return;
    if (!this.#form || !this.#productForm) {
      this.#showError('Unable to add gift presentation.');
      return;
    }

    const giftVariantId = this.dataset.giftVariantId;
    const formData = new FormData(this.#form);
    const merchandiseId = formData.get('id')?.toString();

    if (!giftVariantId || !merchandiseId) {
      this.#showError('Unable to add gift presentation.');
      return;
    }

    const quantity = Math.max(
      1,
      Number(formData.get('quantity')) || Number(this.#productForm.dataset.quantityDefault) || 1
    );
    const note = this.#note?.value.trim() || '';
    const jewelryProperties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match || value instanceof File) continue;
      const propertyName = match[1];
      if (!propertyName || propertyName === 'Gift' || propertyName === 'Gift note') continue;
      if (value.toString().trim()) jewelryProperties[propertyName] = value.toString();
    }

    const giftProperties = {
      For: this.dataset.productTitle || 'BOMBÉ piece',
    };

    if (note) giftProperties['Gift note'] = note;

    const jewelryItem = { id: merchandiseId, quantity };
    if (Object.keys(jewelryProperties).length) jewelryItem.properties = jewelryProperties;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
      .filter(Boolean);

    const payload = {
      items: [
        jewelryItem,
        { id: giftVariantId, quantity: 1, properties: giftProperties },
      ],
    };

    if (sectionIds.length) {
      payload.sections = [...new Set(sectionIds)].join(',');
      payload.sections_url = window.location.pathname;
    }

    const deferredPromise = CartLinesUpdateEvent.createPromise();
    this.#productForm.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          { merchandiseId, quantity },
          { merchandiseId: giftVariantId, quantity: 1 },
        ],
        promise: deferredPromise.promise,
      })
    );

    const addButton = this.#productForm.querySelector('[ref="addToCartButton"]');
    const addButtonWasDisabled = addButton instanceof HTMLButtonElement ? addButton.disabled : false;

    this.#submitting = true;
    this.setAttribute('aria-busy', 'true');
    this.#hideError();
    if (addButton instanceof HTMLButtonElement) addButton.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      const addResponse = await response.json();
      if (!response.ok || addResponse.status) {
        throw new Error(addResponse.description || addResponse.message || 'Unable to add gift presentation.');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!cartResponse.ok) throw new Error('Unable to refresh your bag.');

      const cart = await cartResponse.json();
      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bombe-gift-option',
          sourceId: this.#productForm.id || '',
          itemCount: quantity + 1,
          productId: this.#productForm.dataset.productId,
          sections: addResponse.sections,
          didError: false,
        },
      });

      this.#checkbox.checked = false;
      this.#note.value = '';
      this.#syncState();
    } catch (error) {
      deferredPromise.reject(error);
      const message = error instanceof Error ? error.message : 'Unable to add gift presentation.';
      this.#showError(message);
      this.#productForm.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#submitting = false;
      this.removeAttribute('aria-busy');
      if (addButton instanceof HTMLButtonElement && !addButtonWasDisabled) addButton.disabled = false;
    }
  };

  #showError(message) {
    if (!this.#error) return;
    this.#error.textContent = message;
    this.#error.hidden = false;
  }

  #hideError() {
    if (!this.#error) return;
    this.#error.hidden = true;
    this.#error.textContent = '';
  }
}

if (!customElements.get('bombe-gift-option')) {
  customElements.define('bombe-gift-option', BombeGiftOption);
}
